import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Badge,
  Alert,
  Spinner,
} from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';

import BreadCrumbs from '../../../layouts/breadcrumbs/BreadCrumbs';
import ComponentCard from '../../../components/ComponentCard';
import AppButton from '../../../components/common/AppButton';
import api from '../../../constants/api';
import {
  getStoredUser,
  isSuperAdmin,
  getActiveTenantId,
} from '../../../constants/tenancy';

export default function PaymentGateway() {
  const me = getStoredUser();
  const superAdmin = isSuperAdmin(me);
  const scopedTenantId = getActiveTenantId(me);

  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(scopedTenantId || '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [showKeySecret, setShowKeySecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);

  const [settings, setSettings] = useState({
    tenant_id: '',
    tenant_code: '',
    tenant_name: '',
    gateway_name: 'razorpay',
    is_enabled: false,
    key_id: '',
    key_secret_masked: '',
    is_secret_set: false,
    webhook_secret_masked: '',
    is_webhook_secret_set: false,
    currency: 'INR',
    webhook_url: '',
  });

  const [form, setForm] = useState({
    is_enabled: false,
    key_id: '',
    key_secret: '',
    webhook_secret: '',
    currency: 'INR',
  });

  // Load tenant list for super_admin
  useEffect(() => {
    if (superAdmin) {
      api
        .get('/tenants')
        .then((res) => {
          const list = Array.isArray(res.data?.data) ? res.data.data : [];
          setTenants(list);
          if (!selectedTenantId && list.length > 0) {
            setSelectedTenantId(scopedTenantId || list[0].tenant_id);
          }
        })
        .catch(() => setTenants([]));
    }
  }, [superAdmin, scopedTenantId, selectedTenantId]);

  // Load gateway settings for current or selected tenant
  const loadSettings = useCallback(
    (tenantId) => {
      setLoading(true);
      setTestResult(null);

      const endpoint = tenantId
        ? `/tenants/${tenantId}/payment-gateway?gateway=razorpay`
        : `/tenants/payment-gateway?gateway=razorpay`;

      api
        .get(endpoint)
        .then((res) => {
          const data = res.data?.data || {};
          setSettings(data);
          setForm({
            is_enabled: Boolean(data.is_enabled),
            key_id: data.key_id || '',
            key_secret: '', // Keep empty unless user types a new secret
            webhook_secret: '', // Keep empty unless user types a new secret
            currency: data.currency || 'INR',
          });
        })
        .catch((err) => {
          toast.error(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              'Unable to load payment gateway settings.',
          );
        })
        .finally(() => setLoading(false));
    },
    [],
  );

  useEffect(() => {
    if (superAdmin) {
      if (selectedTenantId) {
        loadSettings(selectedTenantId);
      }
    } else {
      loadSettings();
    }
  }, [superAdmin, selectedTenantId, loadSettings]);

  const handleTenantChange = (e) => {
    const val = e.target.value;
    setSelectedTenantId(val);
  };

  const handleCopyWebhook = () => {
    if (!settings.webhook_url) return;
    navigator.clipboard
      .writeText(settings.webhook_url)
      .then(() => {
        setCopiedWebhook(true);
        toast.success('Webhook URL copied to clipboard!');
        setTimeout(() => setCopiedWebhook(false), 2500);
      })
      .catch(() => {
        toast.info(settings.webhook_url);
      });
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (form.is_enabled) {
      if (!form.key_id.trim()) {
        toast.error('Key ID is required when payment gateway is enabled.');
        return;
      }
      if (!form.key_secret && !settings.is_secret_set) {
        toast.error('Key Secret is required when payment gateway is enabled.');
        return;
      }
    }

    setSaving(true);
    setTestResult(null);

    const targetTenantId = superAdmin ? selectedTenantId : null;
    const endpoint = targetTenantId
      ? `/tenants/${targetTenantId}/payment-gateway`
      : `/tenants/payment-gateway`;

    const payload = {
      gateway_name: 'razorpay',
      is_enabled: form.is_enabled,
      key_id: form.key_id.trim(),
      currency: form.currency || 'INR',
    };

    // Only send secrets if modified
    if (form.key_secret && form.key_secret.trim()) {
      payload.key_secret = form.key_secret.trim();
    }
    if (form.webhook_secret && form.webhook_secret.trim()) {
      payload.webhook_secret = form.webhook_secret.trim();
    }

    api
      .put(endpoint, payload)
      .then(() => {
        toast.success('Payment gateway settings saved successfully!');
        loadSettings(targetTenantId);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            'Failed to save payment gateway settings.',
        );
      })
      .finally(() => setSaving(false));
  };

  const handleTestConnection = () => {
    setTesting(true);
    setTestResult(null);

    const targetTenantId = superAdmin ? selectedTenantId : null;
    const endpoint = targetTenantId
      ? `/tenants/${targetTenantId}/payment-gateway/test`
      : `/tenants/payment-gateway/test`;

    const payload = {
      gateway_name: 'razorpay',
    };

    if (form.key_id.trim()) {
      payload.key_id = form.key_id.trim();
    }
    if (form.key_secret && form.key_secret.trim()) {
      payload.key_secret = form.key_secret.trim();
    }

    api
      .post(endpoint, payload)
      .then((res) => {
        const result = res.data?.data || {};
        setTestResult(result);
        if (result.success) {
          toast.success(result.message || 'Connection successful!');
        } else {
          toast.error(result.message || 'Connection failed.');
        }
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Connection test failed.';
        setTestResult({
          success: false,
          message: errorMsg,
        });
        toast.error(errorMsg);
      })
      .finally(() => setTesting(false));
  };

  return (
    <div>
      <BreadCrumbs
        items={[
          { label: 'Management' },
          { label: 'Payment Gateway' },
        ]}
      />

      {/* Tenant Selector for Super Admin */}
      {superAdmin && (
        <ComponentCard title="Tenant Selection">
          <Row className="align-items-center">
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className="fw-bold">Select Tenant Provider</Label>
                <Input
                  type="select"
                  value={selectedTenantId}
                  onChange={handleTenantChange}
                >
                  {tenants.map((t) => (
                    <option key={t.tenant_id} value={t.tenant_id}>
                      {t.tenant_name} ({t.tenant_code})
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6} className="mt-3 mt-md-0 text-md-end">
              <Badge color={settings.is_enabled ? 'success' : 'secondary'} className="p-2">
                {settings.is_enabled
                  ? 'Online Payments Active'
                  : 'Online Payments Disabled'}
              </Badge>
            </Col>
          </Row>
        </ComponentCard>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Loading gateway configuration...</p>
        </div>
      ) : (
        <Row>
          {/* Main Configuration Card */}
          <Col lg={7}>
            <ComponentCard
              title={
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <Icon.CreditCard size={22} className="text-primary" />
                    <span>Razorpay Settings</span>
                  </div>
                  <Badge color={form.is_enabled ? 'success' : 'secondary'}>
                    {form.is_enabled ? 'Active' : 'Disabled'}
                  </Badge>
                </div>
              }
            >
              <form onSubmit={handleSave}>
                {/* Gateway Toggle Switch */}
                <div className="p-3 mb-4 rounded bg-light border d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-1 fw-bold">Enable Online Payments</h6>
                    <small className="text-muted">
                      Allow customers of this provider to pay via Razorpay (UPI, Cards, Netbanking).
                    </small>
                  </div>
                  <FormGroup switch className="mb-0 ms-3">
                    <Input
                      type="switch"
                      role="switch"
                      checked={form.is_enabled}
                      onChange={(e) =>
                        setForm({ ...form, is_enabled: e.target.checked })
                      }
                      style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                    />
                  </FormGroup>
                </div>

                {/* Key ID */}
                <FormGroup className="mb-3">
                  <Label className="fw-bold">
                    Razorpay Key ID <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="e.g. rzp_live_xxxxxxxx or rzp_test_xxxxxxxx"
                    value={form.key_id}
                    onChange={(e) => setForm({ ...form, key_id: e.target.value })}
                    required={form.is_enabled}
                  />
                  <small className="text-muted">
                    Your public API key from Razorpay Dashboard &gt; Settings &gt; API Keys.
                  </small>
                </FormGroup>

                {/* Key Secret */}
                <FormGroup className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Label className="fw-bold mb-0">
                      Razorpay Key Secret <span className="text-danger">*</span>
                    </Label>
                    {settings.is_secret_set && (
                      <Badge color="info" pill>
                        Secret is saved ({settings.key_secret_masked})
                      </Badge>
                    )}
                  </div>
                  <div className="input-group">
                    <Input
                      type={showKeySecret ? 'text' : 'password'}
                      placeholder={
                        settings.is_secret_set
                          ? '•••••••••••••••• (Leave blank to keep existing)'
                          : 'Enter your Razorpay Key Secret'
                      }
                      value={form.key_secret}
                      onChange={(e) =>
                        setForm({ ...form, key_secret: e.target.value })
                      }
                      required={form.is_enabled && !settings.is_secret_set}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowKeySecret(!showKeySecret)}
                      title={showKeySecret ? 'Hide secret' : 'Show secret'}
                    >
                      {showKeySecret ? <Icon.EyeOff size={16} /> : <Icon.Eye size={16} />}
                    </button>
                  </div>
                  <small className="text-muted">
                    Stored securely using AES-256-GCM encryption. Never exposed in plain text.
                  </small>
                </FormGroup>

                {/* Webhook Secret */}
                <FormGroup className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Label className="fw-bold mb-0">Webhook Secret (Optional)</Label>
                    {settings.is_webhook_secret_set && (
                      <Badge color="info" pill>
                        Webhook Secret saved ({settings.webhook_secret_masked})
                      </Badge>
                    )}
                  </div>
                  <div className="input-group">
                    <Input
                      type={showWebhookSecret ? 'text' : 'password'}
                      placeholder={
                        settings.is_webhook_secret_set
                          ? '•••••••••••••••• (Leave blank to keep existing)'
                          : 'Enter Webhook secret configured in Razorpay'
                      }
                      value={form.webhook_secret}
                      onChange={(e) =>
                        setForm({ ...form, webhook_secret: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                      title={showWebhookSecret ? 'Hide secret' : 'Show secret'}
                    >
                      {showWebhookSecret ? <Icon.EyeOff size={16} /> : <Icon.Eye size={16} />}
                    </button>
                  </div>
                  <small className="text-muted">
                    Used to verify payment status callbacks and notifications from Razorpay.
                  </small>
                </FormGroup>

                {/* Currency */}
                <FormGroup className="mb-4">
                  <Label className="fw-bold">Payment Currency</Label>
                  <Input
                    type="select"
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  >
                    <option value="INR">INR — Indian Rupee (₹)</option>
                    <option value="AED">AED — United Arab Emirates Dirham (د.إ)</option>
                    <option value="USD">USD — US Dollar ($)</option>
                    <option value="SAR">SAR — Saudi Riyal (﷼)</option>
                  </Input>
                  <small className="text-muted">
                    Must match the currency configured on your Razorpay account.
                  </small>
                </FormGroup>

                {/* Test Result Alert */}
                {testResult && (
                  <Alert
                    color={testResult.success ? 'success' : 'danger'}
                    className="d-flex align-items-center gap-2 mb-3"
                  >
                    {testResult.success ? (
                      <Icon.CheckCircle size={18} />
                    ) : (
                      <Icon.AlertTriangle size={18} />
                    )}
                    <div>{testResult.message}</div>
                  </Alert>
                )}

                {/* Buttons */}
                <div className="d-flex align-items-center gap-2 pt-2 border-top">
                  <AppButton
                    type="submit"
                    variant="primary"
                    disabled={saving}
                    icon={saving ? <Spinner size="sm" /> : <Icon.Check size={16} />}
                    label={saving ? 'Saving...' : 'Save Settings'}
                  />

                  <AppButton
                    type="button"
                    variant="outline-secondary"
                    disabled={testing || (!form.key_id && !settings.key_id)}
                    onClick={handleTestConnection}
                    icon={testing ? <Spinner size="sm" /> : <Icon.RefreshCw size={16} />}
                    label={testing ? 'Testing...' : 'Test Connection'}
                  />
                </div>
              </form>
            </ComponentCard>
          </Col>

          {/* Webhook & Setup Instructions Card */}
          <Col lg={5}>
            <ComponentCard
              title={
                <div className="d-flex align-items-center gap-2">
                  <Icon.Globe size={20} className="text-info" />
                  <span>Webhook URL</span>
                </div>
              }
            >
              <p className="text-muted small mb-2">
                Configure this Webhook URL in your <strong>Razorpay Dashboard</strong> under{' '}
                <strong>Settings &gt; Webhooks</strong> so payments made via QR codes and deep links update automatically.
              </p>

              <div className="p-3 bg-light rounded border mb-3">
                <Label className="fw-bold small text-uppercase text-muted">
                  Your Tenant Webhook Endpoint
                </Label>
                <div className="input-group">
                  <Input
                    type="text"
                    readOnly
                    value={settings.webhook_url || 'https://api.fleetnexis.com/...'}
                    className="font-monospace small bg-white"
                  />
                  <button
                    type="button"
                    className={`btn ${copiedWebhook ? 'btn-success' : 'btn-outline-primary'}`}
                    onClick={handleCopyWebhook}
                    title="Copy URL"
                  >
                    {copiedWebhook ? <Icon.Check size={16} /> : <Icon.Copy size={16} />}
                  </button>
                </div>
              </div>

              <h6 className="fw-bold mb-2">Setup Steps in Razorpay:</h6>
              <ol className="small text-muted ps-3 mb-3">
                <li className="mb-1">
                  Log in to your <strong>Razorpay Dashboard</strong>.
                </li>
                <li className="mb-1">
                  Go to <strong>Settings</strong> &gt; <strong>Webhooks</strong> &gt; Click <strong>+ Add New Webhook</strong>.
                </li>
                <li className="mb-1">Paste the Webhook URL above.</li>
                <li className="mb-1">
                  Enter a <strong>Secret</strong> (and copy it into the Webhook Secret field on the left).
                </li>
                <li className="mb-1">Select the following Active Events:</li>
              </ol>

              <div className="d-flex flex-wrap gap-1 mb-3">
                <Badge color="light" className="text-dark border">
                  payment_link.paid
                </Badge>
                <Badge color="light" className="text-dark border">
                  payment.captured
                </Badge>
                <Badge color="light" className="text-dark border">
                  payment.failed
                </Badge>
              </div>

              <Alert color="info" className="small mb-0">
                <Icon.Info size={16} className="me-1" />
                Payments are settled directly into the bank account linked to your Razorpay merchant account.
              </Alert>
            </ComponentCard>
          </Col>
        </Row>
      )}
    </div>
  );
}
